import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { cn } from "../lib/utils.js";
import { ControlContext } from "../lib/control-context.js";
import { shapeClass, type Shape } from "../lib/shape.js";
import { FLOATING_SURFACE, GLASS_SURFACE } from "../lib/surfaces.js";
import { useMediaQuery } from "../hooks/useMediaQuery.js";
import {
  WorkspaceControlDensityProvider,
  type WorkspaceControlDensity,
} from "./workspace-control-density.js";
import { useWorkspaceRightRailTargetWidth } from "./workspace-right-rail-transition.js";
import {
  TRUE_PHONE_VIEWPORT_MAX,
  resolveCollisionFold,
  resolveCollisionStack,
  useWorkspaceControlLayout,
  type WorkspaceControlThresholds,
} from "./workspace-control-layout.js";
import {
  useVisualViewportWidth,
  useWorkspaceControlMeasure,
} from "./workspace-control-measure.js";
import {
  FALLBACK_CONTROL_HEIGHT,
  PHONE_ROW_GAP,
  TOP_EDGE_INSET,
  useWorkspaceTopChromeInset,
} from "./workspace-top-chrome-inset.js";

/**
 * Which corner of the work area the group occupies, or `inline`. Logical, not
 * physical: the `start` corners follow the reading direction, so they mirror in
 * a right-to-left locale along with everything else in the workspace.
 *
 * `inline` sits in flow: no absolute position, no z-index, no corner inset, no
 * phone stacking and no top-chrome inset. Its parent does the placing — a
 * centring row, or the shared Canvas `statusSlot` box, which carries
 * `data-placement="top-start"` itself so a top-end group folds against it.
 */
export type WorkspaceControlPlacement =
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end"
  | "inline";

/**
 * The floating paint (design-owner decision 7: both looks stay). `default` is
 * the workspace rail paint, `glass` the see-through paint over a canvas.
 */
export type WorkspaceControlSurface = "default" | "glass";

/**
 * The group's corner, a C7 shape word (decision 4: floating bars keep their
 * corner, set through a named shape). `rounded` is the shared 8px corner.
 */
export type WorkspaceControlShape = Extract<Shape, "pill" | "rounded">;

export interface WorkspaceControlGroupProps {
  readonly placement: WorkspaceControlPlacement;
  /** Floating paint. Default `default`: today's rail paint. */
  readonly surface?: WorkspaceControlSurface | undefined;
  /** Corner. Default `pill`: today's fully round rail. */
  readonly shape?: WorkspaceControlShape | undefined;
  /**
   * Paint the DS controls inside from the chrome-control rail look: a Button
   * becomes a 32px pill (a 32px circle when iconOnly), primary the 32px
   * primary look, and the ActionMenu trigger follows. A PresenceCluster
   * keeps its own look.
   * Default `false`: every control renders as today.
   */
  readonly railControls?: boolean | undefined;
  /**
   * Accessible name for the cluster, e.g. "Workspace navigation". Required: an
   * unnamed group of icon buttons is announced as an anonymous blob.
   */
  readonly label: string;
  /**
   * The controls. Each reads `useWorkspaceControlDensity()` and renders itself —
   * see that hook for why this is a signal rather than a descriptor array.
   */
  readonly children: ReactNode;
  /**
   * Rendered last, outside the collapse policy, so an overflow menu stays
   * reachable at every width. The group does not populate it; the module decides
   * which of its own items fold in once the stage reaches `overflow`.
   */
  readonly overflowSlot?: ReactNode;
  /**
   * Called with the group's rendered height whenever it changes, so a module can
   * keep its own panels clear of the group without hardcoding a guess. The group
   * reports; it never moves anything else.
   */
  readonly onHeightChange?: (height: number) => void;
  /**
   * Reports the published density to the PARENT, so a module can decide which
   * of its items fold at the narrow stage — the half of the collapse policy the
   * group cannot do for it, because it does not build the items.
   *
   * A callback rather than the context hook, because a module composes this
   * group and therefore sits OUTSIDE its provider: calling
   * `useWorkspaceControlDensity()` there returns the default and silently never
   * collapses. VSM did exactly that, and its two corner groups overlapped by
   * 663px at 375px.
   */
  readonly onDensityChange?: (density: WorkspaceControlDensity) => void;
  /** Module-owned progressive fold ladder; the group publishes only its level. */
  readonly thresholds?: WorkspaceControlThresholds;
  /** Layout only (margin, width, min-width); paint and corner are props. */
  readonly className?: string;
  readonly "data-testid"?: string;
  readonly "data-tour-id"?: string;
  /**
   * Marks this group for `workspace-zone-geometry.mjs` to measure. A declared
   * prop, not a fixture attribute on a child: only the ROOT carries the padding
   * and border, so an inner marker reports a box ~5px smaller per side against
   * a 1px tolerance — clearing a ~10px overlap the gate exists to catch.
   */
  readonly "data-geometry"?: string;
  /**
   * Upper bound on this group's width for that gate. A bounded cluster has to
   * STAY bounded as people join, and "it did not happen to collide today" is
   * not that promise.
   */
  readonly "data-geometry-max-width"?: number;
}

/**
 * The layout of the compact command rail every zone shares, on every placement
 * and surface. The paint comes from `lib/surfaces.ts` and the corner from
 * `lib/shape.ts`.
 *
 * Canvas chrome should read as precise enterprise tooling, not as a stack of
 * oversized floating cards. A fully rounded, elevated token surface and small
 * contact shadow keep the rail distinct from the board without competing with
 * the work on it. The 4px shell padding fits a 32px control row into a 40px
 * overall height.
 *
 * The transition covers the collapse stages — a group narrowing as its labels
 * drop out, or shifting when a rail opens beside it, should move rather than
 * snap. Reduced motion is honoured by the shared media query rather than a prop,
 * since this is decorative movement with no state behind it.
 */
const RAIL_LAYOUT_CLASS =
  "flex items-center gap-0.5 p-1 " +
  "motion-safe:transition-[inset-inline-start,inset-inline-end,max-width,margin,transform,opacity] " +
  "motion-safe:duration-motion-layout motion-safe:ease-motion-standard";

const SURFACE_CLASS: Record<WorkspaceControlSurface, string> = {
  default: FLOATING_SURFACE,
  glass: GLASS_SURFACE,
};

/**
 * One visual contract for neutral actions inside every workspace control rail.
 * Modules still own their controls and behavior; this keeps their 32px height,
 * label weight, spacing, hover fill, and focus treatment from drifting.
 *
 * @deprecated Use `WorkspaceControlButton`, which paints the chrome-control
 * rail look (the same box with the neutral hover; teal marks the selected
 * state only). This string keeps today's classes, teal hover included, and
 * is not derived from the rail look. It leaves the barrel in a later release,
 * once no module uses it.
 */
export const WORKSPACE_CONTROL_BUTTON_CLASS =
  "flex h-8 items-center gap-1 rounded-full px-2 text-sm font-medium text-pb-text transition-[gap,padding,color,background-color] duration-motion-state ease-motion-standard hover:bg-pb-teal-soft hover:text-pb-text focus-ring disabled:pointer-events-none disabled:opacity-50";

/** Self-positioning for the four corners; `inline` leaves it to the parent. */
const PLACEMENT_CLASS: Record<WorkspaceControlPlacement, string> = {
  "top-start": "absolute z-20 top-4 start-4",
  "top-end": "absolute z-20 top-4 end-4",
  "bottom-start": "absolute z-20 bottom-4 start-4",
  "bottom-end": "absolute z-20 bottom-4 end-4",
  inline: "",
};

/**
 * A floating cluster of workspace controls, pinned to one corner of the work
 * area or placed in flow by its parent (`inline`). Owns four things and nothing
 * else: **placement**, the **collapse policy**, **traversal**, and **its own
 * measurement**. It does not render the controls — see
 * `useWorkspaceControlDensity` for that contract and its cost.
 *
 * **Collapse is three stages, based on the containing work area's live width.**
 * Labels sit beside icons on desktop, disappear as a rail takes space, and at
 * phone width the module folds its least-used items into `overflowSlot`.
 * `useMediaQuery` supplies the pre-measurement fallback; the container observer
 * is what makes a widening side rail count. Nothing becomes unreachable because
 * the overflow slot sits outside the policy. An inline group reads the
 * workspace width, not its content-sized parent's (`useWorkspaceControlMeasure`).
 *
 * **A top-end group additionally folds on COLLISION, and drops a row only as
 * the last resort.** It measures the top-start occupant (anything carrying
 * `data-placement="top-start"` inside the workspace) and, the moment the two
 * groups plus a minimum gap no longer fit on one row, folds every ladder slot
 * into `overflowSlot` and hides labels — whatever the width thresholds say.
 * This replaced the width-triggered `stacked-phone` second row, which put the
 * group over canvas content on any desktop narrowed by an open rail. Only when
 * the FOLDED group still does not fit beside the top-start group does it take
 * the second row under it (`data-collision-stacked`), as a true phone does,
 * and only once a top-start group marked `data-can-shrink="true"` has given
 * way as far as it can.
 * The `layout` field still reports `stacked-phone` from the width thresholds
 * for non-colliding consumers, but no module styles a row drop from it.
 *
 * **Traversal is ordinary Tab order, and that is a choice.** A `role="toolbar"`
 * would oblige the APG roving-tabindex contract, and `TabStrip` records why this
 * shell declines it for controls that navigate rather than own tabpanels. The
 * cluster it replaces is plain Tab order today, so muscle memory survives the
 * move. It is a named `role="group"` instead.
 *
 * **It does not offset for an open side panel, and must not.** `EntityWorkspace`
 * mounts the rail as a flex sibling — "push, not overlay" — so the work area has
 * already shrunk by the rail's width before this group is positioned, and
 * `end-4` alone lands it clear of the panel. Reading
 * `--pb-workspace-rail-width` here would subtract that width a second time. The
 * variable exists for `PlayerAILauncher`, which is `position: fixed` and so
 * measures from the viewport, never the shrunk work area — the two look alike
 * and are not.
 */
export function WorkspaceControlGroup({
  placement,
  surface = "default",
  shape = "pill",
  railControls = false,
  label,
  children,
  overflowSlot,
  onHeightChange,
  onDensityChange,
  thresholds,
  className,
  "data-testid": testId,
  "data-tour-id": tourId,
  "data-geometry": geometry,
  "data-geometry-max-width": geometryMaxWidth,
}: WorkspaceControlGroupProps): ReactElement {
  const desktop = useMediaQuery("(min-width: 1280px)");
  const tabletOrWider = useMediaQuery("(min-width: 768px)");
  const settledRailWidth = useWorkspaceRightRailTargetWidth();
  const ref = useRef<HTMLDivElement>(null);
  const measuredWidths = useWorkspaceControlMeasure(
    ref,
    placement,
    settledRailWidth,
  );
  const viewportWidth = useVisualViewportWidth();

  const fallbackViewportWidth =
    typeof window === "undefined" ? 0 : Math.max(window.innerWidth, 0);
  const mediaWidth = desktop
    ? Math.max(fallbackViewportWidth, 1280)
    : tabletOrWider
      ? Math.min(Math.max(fallbackViewportWidth, 768), 1279)
      : Math.min(fallbackViewportWidth || 375, 767);
  const widthDensity = useWorkspaceControlLayout({
    containerWidth: measuredWidths?.container ?? mediaWidth,
    movingRailWidth: measuredWidths?.rail ?? 0,
    railTargetWidth: settledRailWidth,
    ...(thresholds ? { thresholds } : {}),
  });

  // Collision fold (top-end groups): when this group plus the top-start
  // occupant no longer fit on one row, everything foldable moves into the
  // overflow menu and labels drop, instead of the group dropping to a second
  // row over canvas content. `naturalWidthRef` remembers the width the group
  // held while unfolded — while folded, the live width would understate what
  // an unfold needs, so the release test uses the remembered value (see
  // resolveCollisionFold's hysteresis).
  const [collisionFolded, setCollisionFolded] = useState(false);
  // Folded and STILL colliding (QUAL-BUG-8): a narrow work area beside an open
  // rail, with a top-start group that cannot shrink. Only then does the group
  // take the second row a true phone uses; overlapping the other group would
  // put its subtree over that group's buttons.
  const [collisionStacked, setCollisionStacked] = useState(false);
  const naturalWidthRef = useRef(0);
  const foldCap = thresholds?.foldAt?.length ?? 0;
  const phoneStacked =
    placement === "top-end" &&
    viewportWidth > 0 &&
    viewportWidth < TRUE_PHONE_VIEWPORT_MAX;
  const stacked = phoneStacked || collisionStacked;
  useLayoutEffect(() => {
    if (placement !== "top-end" || !measuredWidths) return;
    if (!collisionFolded) naturalWidthRef.current = measuredWidths.self;
    const next = resolveCollisionFold({
      containerWidth: widthDensity.availableWidth,
      counterpartWidth: measuredWidths.counterpart,
      naturalWidth: naturalWidthRef.current,
      folded: collisionFolded,
    });
    if (next !== collisionFolded) setCollisionFolded(next);
    // Only a group whose fold has rendered knows its folded width. Read it
    // live: this effect runs after that render, while the measured width can
    // still hold the unfolded value until the next observer callback (and a
    // fold that only restyles its children triggers none), which would stack
    // a group that fits.
    const nextStacked =
      next &&
      collisionFolded &&
      resolveCollisionStack({
        containerWidth: widthDensity.availableWidth,
        counterpartWidth: measuredWidths.counterpart,
        counterpartCanShrink: measuredWidths.counterpartCanShrink,
        foldedWidth:
          ref.current?.getBoundingClientRect().width ?? measuredWidths.self,
        stacked: collisionStacked,
      });
    if (nextStacked !== collisionStacked) setCollisionStacked(nextStacked);
  }, [
    collisionFolded,
    collisionStacked,
    measuredWidths,
    placement,
    widthDensity.availableWidth,
  ]);

  const density = useMemo<WorkspaceControlDensity>(() => {
    if (stacked) {
      return {
        ...widthDensity,
        labels: "hidden",
        foldLevel: Math.max(widthDensity.foldLevel, foldCap),
        layout: "stacked-phone",
      };
    }
    return collisionFolded
      ? {
          ...widthDensity,
          labels: "hidden",
          foldLevel: Math.max(widthDensity.foldLevel, foldCap),
          layout: "single-row",
        }
      : widthDensity;
  }, [collisionFolded, foldCap, stacked, widthDensity]);

  useEffect(() => {
    onDensityChange?.(density);
  }, [density, onDensityChange]);

  // Report the height on mount and whenever the stage changes shape. A
  // ResizeObserver also catches a module swapping its own children; fall back to
  // a direct read where there is none (jsdom, any server render).
  useEffect(() => {
    const node = ref.current;
    if (!node || !onHeightChange) return;
    const report = (): void =>
      onHeightChange(node.getBoundingClientRect().height);
    report();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(report);
    observer.observe(node);
    return () => observer.disconnect();
  }, [onHeightChange, density]);

  useWorkspaceTopChromeInset(
    ref,
    placement !== "inline",
    density,
    measuredWidths?.counterpartHeight,
    stacked,
  );

  const phoneTop =
    TOP_EDGE_INSET +
    Math.max(measuredWidths?.counterpartHeight ?? 0, FALLBACK_CONTROL_HEIGHT) +
    PHONE_ROW_GAP;

  return (
    <div
      ref={ref}
      role="group"
      tabIndex={-1}
      aria-label={label}
      data-placement={placement}
      data-labels={density.labels}
      data-stage={density.stage}
      data-layout={density.layout}
      data-fold-level={density.foldLevel}
      data-phone-stacked={phoneStacked || undefined}
      data-collision-stacked={collisionStacked || undefined}
      data-tour-id={tourId}
      {...(testId ? { "data-testid": testId } : {})}
      {...(geometry ? { "data-geometry": geometry } : {})}
      {...(geometryMaxWidth !== undefined
        ? { "data-geometry-max-width": geometryMaxWidth }
        : {})}
      className={cn(
        "pointer-events-auto",
        PLACEMENT_CLASS[placement],
        RAIL_LAYOUT_CLASS,
        shapeClass(shape),
        SURFACE_CLASS[surface],
        className,
      )}
      style={stacked ? { top: `${phoneTop}px` } : undefined}
    >
      <WorkspaceControlDensityProvider value={density}>
        <ControlContext.Provider value={railControls ? "rail" : null}>
          {children}
          {overflowSlot}
        </ControlContext.Provider>
      </WorkspaceControlDensityProvider>
    </div>
  );
}
